import { useMemo } from 'react'
import { CheckIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { useAuthStore } from '@/features/auth/store/store.ts'
import {
	OpsVettingCardGrid,
	OpsVettingDisabledContainer,
	OpsVettingParagraph,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { ServiceVettingCriteriaOfferingCard } from '@/features/service-vetting/components/service-vetting-criteria-offering-card'
import type {
	RemappedOffering,
	RemappedService
} from '@/features/service-vetting/types.ts'
import { useTranslation } from '@/translations/i18n'

export const ServiceVettingCriteriaOfferings = ({
	service
}: {
	service: RemappedService
}) => {
	const { t } = useTranslation()
	const user = useAuthStore((state) => state.user)

	const primaryOfferings: RemappedOffering[] = useMemo(
		() => service.offerings.filter((offering) => offering.type !== 'other'),
		[service.offerings]
	)

	const otherOfferings: RemappedOffering[] = useMemo(
		() => service.offerings.filter((offering) => offering.type === 'other'),
		[service.offerings]
	)

	const hideAction =
		service.status !== 'under_review' ||
		user?.role === 'read_only' ||
		service.is_host_modified

	return (
		<OpsVettingSectionWrapper direction={'column'} fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<OpsVettingSectionTitle>
					{t('vetting:criteria.service.offerings.title')}
				</OpsVettingSectionTitle>
				<OpsVettingParagraph>
					{service.offerings.filter((offering) => !offering.status)
						.length > 0 ? (
						t(
							'vetting:criteria.service.offerings.offerings_remaining',
							{
								value: service.offerings.filter(
									(offering) => !offering.status
								).length
							}
						)
					) : (
						<span className='flex gap-1'>
							<CheckIcon className='fill-success-600' size={20} />
							{t('vetting:criteria.commons.reviewed')}
						</span>
					)}
				</OpsVettingParagraph>
			</div>
			<Separator orientation='horizontal' />
			<OpsVettingDisabledContainer
				$isDisabled={service.expertise.status === 'declined'}
			>
				<OpsVettingCardGrid>
					{primaryOfferings.map((offering, index) => (
						<ServiceVettingCriteriaOfferingCard
							galleryId={service.gallery.id}
							hideAction={hideAction}
							index={index}
							key={offering.id}
							offering={offering}
						/>
					))}
					{otherOfferings.map((offering, index) => (
						<ServiceVettingCriteriaOfferingCard
							galleryId={service.gallery.id}
							hideAction={hideAction}
							index={index}
							key={offering.id}
							offering={offering}
						/>
					))}
				</OpsVettingCardGrid>
			</OpsVettingDisabledContainer>
		</OpsVettingSectionWrapper>
	)
}
